/** Extract a user-facing message from a failed API response (Problem JSON or legacy shapes). */
const TECHNICAL_ERROR = /must not be (blank|null)|HTTP\s*\d+|constraint-violation|Field error/i;
const GENERIC_ACCESS_DENIED =
    /you do not have permission to access this resource|^(forbidden|access denied|unauthorized)$/i;

export type MutationErrorBody = {
    detail?: unknown;
    message?: unknown;
};

export function isGenericAccessDeniedText(text?: string | null): boolean {
    const trimmed = text?.trim() ?? "";
    return !trimmed || GENERIC_ACCESS_DENIED.test(trimmed);
}

/**
 * CSRF and Spring AccessDeniedHandler 403s have no useful `detail`.
 * Real RBAC from ForbiddenException keeps a specific detail.
 */
export function mutationErrorFromBody(
    status: number,
    body: MutationErrorBody,
    retryFallback: string,
    permissionFallback = retryFallback,
): string {
    const detail = typeof body.detail === "string" ? body.detail.trim() : "";
    const message = typeof body.message === "string" ? body.message.trim() : "";
    if (status === 403) {
        if (/csrf/i.test(detail) || /csrf/i.test(message)) {
            return retryFallback;
        }
        if (detail && !isGenericAccessDeniedText(detail) && !TECHNICAL_ERROR.test(detail)) {
            return detail;
        }
        if (message && !isGenericAccessDeniedText(message) && !TECHNICAL_ERROR.test(message)) {
            return message;
        }
        return retryFallback;
    }
    if (detail && !TECHNICAL_ERROR.test(detail)) {
        return detail;
    }
    if (message && !TECHNICAL_ERROR.test(message) && !isGenericAccessDeniedText(message)) {
        return message;
    }
    return permissionFallback;
}

export async function parseApiErrorMessage(
    response: Response,
    fallback = "Something went wrong. Please try again.",
): Promise<string> {
    try {
        const data = (await response.json()) as Record<string, unknown>;
        const detail = typeof data.detail === "string" ? data.detail : undefined;
        const message = typeof data.message === "string" ? data.message : undefined;
        const error = typeof data.error === "string" ? data.error : undefined;
        if (response.status === 403) {
            return mutationErrorFromBody(403, { detail, message }, fallback);
        }
        const text = detail || message || error;
        if (!text || TECHNICAL_ERROR.test(text) || isGenericAccessDeniedText(text)) {
            return fallback;
        }
        return text;
    } catch {
        return fallback;
    }
}
